import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import {
    ShieldCheck,
    CreditCard,
    Calendar,
    MapPin,
    AlertCircle,
    CheckCircle2,
    Edit,
    Globe
} from "lucide-react";

const PassportForm = ({ preview, formData, handleChange, loading, errors, formatDate, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localErrors, setLocalErrors] = useState({});


    const validateForm = () => {
        const newErrors = {};
        if (!formData.passportNumber?.trim()) newErrors.passportNumber = "Passport number is required";
        if (!formData.placeOfIssue?.trim()) newErrors.placeOfIssue = "Place of issue is required";
        if (!formData.dateOfIssue) newErrors.dateOfIssue = "Date of issue is required";
        if (!formData.dateOfExpiry) newErrors.dateOfExpiry = "Date of expiry is required";

        if (formData.dateOfIssue && formData.dateOfExpiry && formData.dateOfIssue > formData.dateOfExpiry) {
            newErrors.dateOfExpiry = "Expiry date must be after issue date";
        }

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* HEADER SECTION */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white relative overflow-hidden shadow-xl">
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 sm:gap-6">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-white/10 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                            <ShieldCheck size={24} className="text-blue-300 sm:w-[30px] sm:h-[30px]" />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Passport Details</h1>
                            <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5 sm:mt-1">Official travel document information</p>
                        </div>
                    </div>
                </div>
                <Globe className="absolute -right-8 -bottom-8 sm:-right-10 sm:-bottom-10 text-white/5 w-32 h-32 sm:w-48 sm:h-48" />
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-4 sm:p-6 md:p-8">

                {/* ERROR DISPLAY */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 sm:mb-8 bg-rose-50 border-l-4 border-rose-500 p-4 sm:p-5 rounded-r-xl sm:rounded-r-2xl animate-in fade-in duration-300">
                        <div className="flex items-center gap-2 text-rose-800 font-semibold mb-2 text-sm sm:text-base">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="text-xs sm:text-sm text-rose-600 space-y-1 ml-4 sm:ml-6 list-disc">
                            {Object.values(localErrors).map((e, i) => <li key={i}>{e}</li>)}
                            {errors?.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
                        </ul>
                    </div>
                )}

                {preview && !isEditing && preview.passportNumber && !loading ? (
                    /* PREVIEW MODE */
                    <div className="space-y-6 sm:space-y-8 md:space-y-10 animate-in fade-in zoom-in-95 duration-500">
                        {/* Status Banner for Approved */}
                        {preview.status === "approve" && (
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-green-100 flex items-center gap-3">
                                <CheckCircle2 size={20} className="text-green-600 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-green-800">Passport Verified</p>
                                    <p className="text-xs text-green-700">Your passport information has been verified and approved.</p>
                                </div>
                            </div>
                        )}

                        {/* Info Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                            <PreviewItem icon={<CreditCard className="text-blue-500" />} label="Passport Number" value={preview.passportNumber} />
                            <PreviewItem icon={<MapPin className="text-emerald-500" />} label="Place of Issue" value={preview.placeOfIssue} />
                            <PreviewItem icon={<Calendar className="text-indigo-500" />} label="Date of Issue" value={formatDate(preview.dateOfIssue)} />
                            <PreviewItem icon={<Calendar className="text-rose-500" />} label="Date of Expiry" value={formatDate(preview.dateOfExpiry)} />
                        </div>

                        {/* Action Buttons */}
                        {(preview.status === "reject" || !preview.status) && (
                            <div className="pt-6 sm:pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-center gap-3">
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold transition-all shadow-md active:scale-95"
                                >
                                    <Edit size={18} /> Modify Information
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* FORM MODE */
                    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                            <Input
                                label="Passport Number"
                                icon={<CreditCard size={18} />}
                                placeholder="A1234567"
                                value={formData.passportNumber}
                                onChange={(v) => handleChange("passportNumber", v)}
                                error={localErrors.passportNumber}
                                required
                            />
                            <Input
                                label="Place of Issue"
                                icon={<MapPin size={18} />}
                                placeholder="e.g., Mumbai, India"
                                value={formData.placeOfIssue}
                                onChange={(v) => handleChange("placeOfIssue", v)}
                                error={localErrors.placeOfIssue}
                                required
                            />
                            <Input
                                type="date"
                                label="Date of Issue"
                                icon={<Calendar size={18} />}
                                value={formData.dateOfIssue}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(v) => handleChange("dateOfIssue", v)}
                                error={localErrors.dateOfIssue}
                                required
                            />
                            <Input
                                type="date"
                                label="Date of Expiry"
                                icon={<Calendar size={18} />}
                                value={formData.dateOfExpiry}
                                min={formData.dateOfIssue || new Date().toISOString().split('T')[0]}
                                onChange={(v) => handleChange("dateOfExpiry", v)}
                                error={localErrors.dateOfExpiry}
                                required
                            />
                        </div>


                    </form>
                )}
            </div>
        </div>
    );
};

/* ENHANCED REUSABLE INPUT */
const Input = ({ label, value, onChange, type = "text", icon, placeholder, required, max, min, error }) => (
    <div className="space-y-1.5 sm:space-y-2">
        <label className="text-xs sm:text-sm font-semibold text-gray-700 ml-1 flex items-center gap-1">
            {label} {required && <span className="text-rose-500 text-sm">*</span>}
        </label>
        <div className="relative group">
            <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                {icon}
            </div>
            <input
                type={type}
                value={value}
                max={max}
                min={min}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className={`
                    w-full bg-gray-50 border-2 rounded-xl sm:rounded-2xl 
                    pl-9 sm:pl-12 pr-4 py-2.5 sm:py-3.5 
                    focus:bg-white focus:border-blue-500 outline-none transition-all 
                    text-sm sm:text-base text-gray-800 placeholder:text-gray-400
                    ${error ? "border-rose-300 bg-rose-50/30 focus:border-rose-500" : "border-gray-100 focus:border-blue-500"}
                `}
            />
        </div>
        {error && <p className="text-rose-500 text-[10px] sm:text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}
    </div>
);

/* ENHANCED PREVIEW ITEM */
const PreviewItem = ({ icon, label, value }) => (
    <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 md:p-5 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 hover:bg-white hover:shadow-md transition-all duration-300 group">
        <div className="p-2 sm:p-3 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 text-gray-500 group-hover:text-blue-600 transition-colors shrink-0">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[9px] sm:text-[10px] uppercase font-semibold text-gray-400 tracking-wider leading-none mb-1">
                {label}
            </p>
            <p className="text-gray-800 font-semibold text-sm sm:text-base break-words">
                {value || <span className="text-gray-300">—</span>}
            </p>
        </div>
    </div>
);

export default React.memo(PassportForm);